import { useEffect, useState } from "react";
import { ReceiptText, WalletCards } from "lucide-react";
import { Link } from "react-router-dom";

import api from "../../lib/api";
import { Loading } from "../../components/shared";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(value || 0);

export default function ResidentFinance() {
  const [data, setData] = useState(null);
  const [bills, setBills] = useState([]);
  const [error, setError] = useState("");
  const [receipts, setReceipts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/resident/finance/summary"),
      api.get("/resident/finance/bills"),
      api.get("/resident/finance/receipts").catch(() => ({ data: [] })),
    ])
      .then(([summary, billRows, receiptRows]) => {
        setData(summary.data);
        setBills(billRows.data);
        setReceipts(receiptRows.data);
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.detail || "Financial information is unavailable.",
        );
      });
  }, []);

  if (error) return <p data-testid="resident-finance-error">{error}</p>;
  if (!data) return <Loading />;

  return (
    <div data-testid="resident-finance-page">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        My Finances
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold">Selected home finances</h1>
      <section className="mt-7 grid gap-4 sm:grid-cols-3">
        <Metric
          icon={WalletCards}
          label="Outstanding"
          testId="resident-finance-outstanding"
          value={money(data.outstanding)}
        />
        <Metric
          icon={ReceiptText}
          label="Advance credit"
          testId="resident-finance-advance"
          value={money(data.advance_credit)}
        />
        <Metric
          icon={ReceiptText}
          label="Bills"
          testId="resident-finance-bill-count"
          value={bills.length}
        />
      </section>
      <List kind="bills" rows={bills} testId="resident-finance-bills" title="Bills" />
      <List
        kind="receipts"
        rows={receipts}
        testId="resident-finance-receipts"
        title="Receipts"
      />
      <Link
        className="mt-6 inline-block text-sm text-primary"
        data-testid="resident-ledger-link"
        to="/resident/finance/ledger"
      >
        View account statement
      </Link>
    </div>
  );
}

function Metric({ icon: Icon, label, value, testId }) {
  return (
    <article className="border border-slate-200 bg-white p-5" data-testid={testId}>
      <Icon className="h-5 w-5 text-primary" />
      <p className="mt-5 text-2xl font-semibold">{value}</p>
      <p className="text-sm text-slate-600">{label}</p>
    </article>
  );
}

function List({ kind, rows, testId, title }) {
  return (
    <section className="mt-8" data-testid={testId}>
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <div className="mt-3 space-y-2">
        {rows.length ? (
          rows.map((row) => {
            const id = row.bill_id || row.receipt_id;
            return (
              <Link
                className="block border border-slate-200 bg-white p-4"
                data-testid={`resident-finance-${kind}-${id}`}
                key={id}
                to={`/resident/finance/${kind}/${id}`}
              >
                <p className="font-medium">{row.bill_number || row.receipt_number}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {row.status} · {row.due_date || row.date}
                </p>
                <p className="mt-2 text-sm">
                  {kind === "bills" ? money(row.outstanding) : money(row.amount)}
                </p>
              </Link>
            );
          })
        ) : (
          <p
            className="border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600"
            data-testid={`resident-finance-${kind}-empty`}
          >
            No records are available for this home.
          </p>
        )}
      </div>
    </section>
  );
}